import { Box, Stack, Typography, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import { useTranslation } from "react-i18next";
import { PASSWORD_RULES, checkPassword } from "@opendialysis/procare-api-schemas";

/**
 * Live checklist of the password policy.
 *
 * The rules come from the shared contract, so this cannot claim a password is
 * acceptable that the API then refuses — the failure mode of hand-copying them
 * is a green tick next to a 400.
 */
export function PasswordRequirements({ value }: { value: string }) {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const met = checkPassword(value ?? "");

  return (
    <Box
      sx={{
        bgcolor: "action.hover",
        borderRadius: 2,
        p: 2,
        display: "flex",
        gap: 1.5,
      }}
    >
      <GppGoodOutlinedIcon color="primary" />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle2" component="p" sx={{ mb: 1 }}>
          {t("password.requirementsTitle")}
        </Typography>
        {/* polite, not assertive: this updates on every keystroke and must not
            interrupt what a screen reader is already saying. */}
        <Stack component="ul" spacing={0.5} aria-live="polite" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {PASSWORD_RULES.map((rule) => {
            const satisfied = met[rule.id];
            return (
              <Stack
                key={rule.id}
                component="li"
                direction="row"
                spacing={1}
                alignItems="center"
              >
                {satisfied ? (
                  <CheckIcon
                    fontSize="small"
                    sx={{ color: theme.palette.success.main }}
                  />
                ) : (
                  <CircleOutlinedIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                )}
                <Typography
                  variant="body2"
                  color={satisfied ? "text.primary" : "text.secondary"}
                >
                  {t(`password.rules.${rule.id}`)}
                </Typography>
                {/* The icon alone is colour-only information. */}
                <Box component="span" sx={visuallyHidden}>
                  {t(satisfied ? "password.ruleMet" : "password.ruleUnmet")}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}

const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;
